import { Box, Grid, useTheme, useMediaQuery, Typography } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import React from 'react';
import { useTranslation } from 'react-i18next';
import SimpleAlert from '../../../../../components/display/SimpleAlert';
import FeedbackCard from './FeedbackCard';

export default function FeedbackComponentWrapper({ title, fetch, noDataMessage, type = "clinic", apiKey }) {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    
    const pages = fetch?.data?.pages;
    const content = pages?.flatMap((page) => page?.content) ?? [];

    // Responsive items per row
    const getItemsPerRow = () => {
        if (isMobile) return 1;
        if (isTablet) return 2;
        return 3;
    };

    const itemsPerRow = getItemsPerRow();
    const rowCount = Math.ceil(content.length / itemsPerRow);

    const scrollRef = React.useRef(null);

    // Responsive row height estimation
    const getRowHeight = () => {
        if (isMobile) return 280; // More space for single column
        if (isTablet) return 240;
        return 220;
    };

    const virtualizer = useVirtualizer({
        count: rowCount,
        estimateSize: () => getRowHeight(),
        getScrollElement: () => scrollRef.current,
        overscan: isMobile ? 3 : 5, // Less overscan on mobile for performance
    });

    const virtualRows = virtualizer.getVirtualItems();

    React.useEffect(() => {
        const lastRow = virtualRows[virtualRows.length - 1];
        if (!fetch.hasNextPage || fetch.isFetchingNextPage || !lastRow) return;
        const lastItemIndex = (lastRow.index + 1) * itemsPerRow - 1;
        if (lastItemIndex >= content.length - 1) {
            fetch.fetchNextPage();
        }
    }, [virtualRows, content.length, fetch, itemsPerRow]);

    if (!content?.length) {
        return <NoData message={noDataMessage} />;
    }

    return (
        <Box
            ref={scrollRef}
            sx={{
                height: isMobile ? '500px' : '600px', 
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: isMobile ? 'none' : 'auto',
                '&::-webkit-scrollbar': {
                    display: isMobile ? 'none' : 'block',
                },
            }}
        >
            <div
                style={{
                    height: `${virtualizer.getTotalSize()}px`,
                    position: 'relative',
                    width: '100%',
                }}
            >
                {virtualRows.map((vRow) => {
                    const startIndex = vRow.index * itemsPerRow;
                    const rowItems = content.slice(startIndex, startIndex + itemsPerRow);

                    return (
                        <div
                            key={vRow.key}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${vRow.start}px)`,
                                height: `${vRow.size}px`,
                                display: 'flex',
                                gap: isMobile ? '8px' : '16px',
                                padding: isMobile ? '4px 8px' : '8px 16px',
                            }}
                        >
                            {rowItems.map((item, colIdx) => (
                                <Box 
                                    key={`${vRow.index}-${colIdx}`} 
                                    sx={{ 
                                        flex: 1, 
                                        minWidth: 0,
                                        // flexBasis: isMobile ? '100%' : 'auto',
                                    }}
                                >
                                    <FeedbackCard 
                                        feedback={item} 
                                        type={type} 
                                        apiKey={apiKey} 
                                        isMobile={isMobile}
                                    />
                                </Box>
                            ))}
                            {/* Fill empty slots to maintain layout */}
                            {rowItems.length < itemsPerRow && 
                                Array.from({ length: itemsPerRow - rowItems.length }).map((_, emptyIdx) => (
                                    <Box 
                                        key={`empty-${emptyIdx}`} 
                                        sx={{ 
                                            flex: 1, 
                                            minWidth: 0,
                                            flexBasis: isMobile ? '100%' : 'auto',
                                            visibility: 'hidden' 
                                        }} 
                                    />
                                ))
                            }
                        </div>
                    );
                })}
            </div>
            
            {/* Loading indicator for better UX */}
            {fetch.isFetchingNextPage && (
                <Box 
                    sx={{ 
                        textAlign: 'center', 
                        py: 2,
                        px: isMobile ? 1 : 2
                    }}
                >
                    <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                    >
                        {t('LoadingMore')}...
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

function NoData({ message }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    return (
        <Box sx={{ p: isMobile ? 1 : 2 }}>
            <SimpleAlert 
                severity="info" 
                message={message} 
                sx={{
                    '& .MuiAlert-message': {
                        fontSize: isMobile ? '0.875rem' : '1rem',
                        p: isMobile ? 1 : 2
                    }
                }}
            />
        </Box>
    );
}

// Optional: Add a mobile-friendly loading skeleton
export function FeedbackSkeleton({ isMobile = false }) {
    return (
        <Box 
            sx={{ 
                p: isMobile ? 1 : 2,
                height: isMobile ? 250 : 200,
                bgcolor: 'grey.100',
                borderRadius: 2,
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                    '0%': { opacity: 0.6 },
                    '50%': { opacity: 0.8 },
                    '100%': { opacity: 0.6 },
                }
            }}
        />
    );
}